import { useState } from "react";
import { toast } from "sonner";
import { User, Lock, Save, Eye, EyeOff, CheckCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS, normalizeRole } from "@/lib/rbac";
import { Header } from "./Schools";

const SI = "w-full px-3 py-2.5 rounded-lg border border-gray-200 outline-none focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 text-sm bg-white";
const PW_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#_\-])[A-Za-z\d@$!%*?&^#_\-]{8,72}$/;

function validatePassword(pw) {
  if (!pw) return "Password is required.";
  if (!PW_REGEX.test(pw)) return "Min 8 chars with uppercase, lowercase, number, and special character (@$!%*?&^#_-).";
  return null;
}

function PasswordInput({ value, onChange, placeholder = "Password", autoComplete = "new-password" }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} value={value} onChange={onChange}
        placeholder={placeholder} autoComplete={autoComplete} className={`${SI} pr-10`} />
      <button type="button" onClick={() => setShow((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center text-gray-400 hover:text-gray-600">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function StrengthBar({ password }) {
  if (!password) return null;
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[@$!%*?&^#_\-]/.test(password)) score++;
  const labels = ["", "Weak", "Fair", "Good", "Strong", "Very Strong"];
  const colors = ["", "bg-red-400", "bg-orange-400", "bg-yellow-400", "bg-green-400", "bg-emerald-500"];
  return (
    <div className="mt-1.5">
      <div className="flex gap-1">{[1,2,3,4,5].map((n) => <div key={n} className={`h-1 flex-1 rounded-full ${score >= n ? colors[score] : "bg-gray-200"}`} />)}</div>
      <p className="text-xs text-gray-400 mt-0.5">{labels[score]}</p>
    </div>
  );
}

const Field = ({ label, error, children }) => (
  <div>
    <label className="text-sm font-medium text-gray-700 block mb-1.5">{label}</label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

// ── Profile Section ───────────────────────────────────────────────────────────
function ProfileSection({ user, onRefresh }) {
  const [form, setForm] = useState({ name: user?.name || "", mobile: user?.mobile || "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Full name is required."); return; }
    setLoading(true);
    try {
      await api.patch("/auth/me", { name: form.name.trim(), mobile: form.mobile.trim() });
      await onRefresh();
      toast.success("Profile updated.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed."); }
    setLoading(false);
  };

  const roleNorm  = normalizeRole(user?.role || "");
  const roleLabel = ROLE_LABELS[roleNorm] || user?.role;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 grid place-items-center text-white text-xl font-bold shrink-0">
          {(user?.name || "?")[0].toUpperCase()}
        </div>
        <div>
          <div className="font-semibold text-[#0F172A]">{user?.name}</div>
          <div className="text-sm text-gray-500">{user?.email}</div>
          <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#EEF2FF] text-[#4F46E5] border border-[#C7D2FE]">
            {roleLabel}
          </span>
        </div>
      </div>

      <form onSubmit={submit} className="grid gap-4 max-w-md">
        <Field label="Full name">
          <input value={form.name} onChange={set("name")} className={SI} />
        </Field>
        <Field label="Email address">
          <input value={user?.email || ""} readOnly disabled className={`${SI} bg-gray-50 cursor-not-allowed text-gray-500`} />
          <p className="text-xs text-gray-400 mt-1">Email cannot be changed from here. Contact a Super Admin.</p>
        </Field>
        <Field label="Mobile number">
          <input type="tel" value={form.mobile} onChange={set("mobile")} placeholder="+91 99999 00000" className={SI} />
        </Field>
        <div>
          <p className="text-xs text-gray-400 mb-1">Role</p>
          <div className="px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-600">{roleLabel}</div>
        </div>

        <button type="submit" disabled={loading}
          className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-[#1e293b] disabled:opacity-50 w-fit">
          <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save profile"}
        </button>
      </form>
    </div>
  );
}

// ── Password Section ──────────────────────────────────────────────────────────
function PasswordSection() {
  const [form, setForm] = useState({ current: "", newPw: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    const errs = {};
    if (!form.current) errs.current = "Current password is required.";
    const pwErr = validatePassword(form.newPw);
    if (pwErr) errs.newPw = pwErr;
    if (form.newPw !== form.confirm) errs.confirm = "Passwords do not match.";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await api.patch("/auth/me/password", { current_password: form.current, new_password: form.newPw });
      setSuccess(true);
      setForm({ current: "", newPw: "", confirm: "" });
      setErrors({});
      toast.success("Password changed successfully.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Password change failed.";
      if (msg.toLowerCase().includes("incorrect")) {
        setErrors({ current: "Current password is incorrect." });
      } else {
        toast.error(msg);
      }
    }
    setLoading(false);
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6">
      <div className="flex items-center gap-2 mb-6">
        <Lock className="w-5 h-5 text-[#4F46E5]" />
        <h2 className="font-semibold text-[#0F172A]">Change password</h2>
      </div>

      {success && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-4 text-sm text-emerald-700">
          <CheckCircle className="w-4 h-4 shrink-0" /> Password changed successfully.
        </div>
      )}

      <form onSubmit={submit} className="grid gap-4 max-w-md">
        <Field label="Current password" error={errors.current}>
          <PasswordInput value={form.current} onChange={set("current")} placeholder="Enter current password" autoComplete="current-password" />
        </Field>
        <Field label="New password" error={errors.newPw}>
          <PasswordInput value={form.newPw} onChange={set("newPw")} placeholder="Enter new password" />
          <StrengthBar password={form.newPw} />
        </Field>
        <Field label="Confirm new password" error={errors.confirm}>
          <PasswordInput value={form.confirm} onChange={set("confirm")} placeholder="Repeat new password" />
        </Field>
        <button type="submit" disabled={loading}
          className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-[#1e293b] disabled:opacity-50 w-fit">
          <Lock className="w-4 h-4" /> {loading ? "Changing…" : "Change password"}
        </button>
      </form>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function MyProfile() {
  const { user, refresh } = useAuth();

  return (
    <div className="fade-up">
      <Header title="My Profile" subtitle="Update your name, mobile, and password." />
      <div className="mt-6 grid gap-6 max-w-2xl">
        <ProfileSection user={user} onRefresh={refresh} />
        <PasswordSection />
      </div>
    </div>
  );
}
